import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Toolbar,
  Stack,
  Backdrop,
  Typography,
  CircularProgress,
  Divider,
  Button,
  Breadcrumbs,
  Link,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useParams, useNavigate } from 'react-router';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/Warning';
import UpdateIcon from '@mui/icons-material/Update';
import EngineeringIcon from '@mui/icons-material/Engineering';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReplayIcon from '@mui/icons-material/Replay';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import {
  fetchRig,
  deleteRig,
  disableRemoteSupport,
  enableRemoteSupport,
  runAction,
  toggleMaintenance,
  updateRig,
  reinstallNode,
  replaceNode,
  getNodeLatestVersion,
  upgradeNode,
} from '@/actions/rigs';
import { fetchActiveProvider } from '@/actions/providers';
import { fetchActiveRegion } from '@/actions/regions';
import { clearErrorMessage } from '@/actions/errorMessage';

import toNiceTimestamp from '@/util/toNiceTimestamp';
import readableBytes from '@/util/readableBytes';
import STATUSES from '@/components/statuses';
import PROVIDER_TYPES from '@/components/resources/providerTypes';
import MinionIdField from '@/components/resources/RegionDashboard/NodeForm/PhysicalNodeForm/MinionIdField';
import RemoveEphemeralNodeDialog from '@/components/resources/RegionDashboard/Nodes/RemoveEphemeralNodeDialog';
import RIG_TYPES from '@/components/resources/RegionDashboard/Nodes/rigTypes';

export default function Node() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { provider_uuid, region_uuid, rig_uuid } = useParams();

  const api_error = useSelector((state) => state.errorMessage);
  const provider = useSelector((state) => state.provider);
  const region = useSelector((state) => state.region);
  const rigs = useSelector((state) => state.rigs);

  const [node, setNode] = useState(
    rigs ? rigs.find((rig) => rig.rig_uuid === rig_uuid) : undefined,
  );
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [reinstallDialogOpen, setReinstallDialogOpen] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [removeEphemeralDialogOpen, setRemoveEphemeralDialogOpen] =
    useState(false);
  const [minionId, setMinionId] = useState('');
  const [minionIdError, setMinionIdError] = useState(false);
  const [fetchAttempted, setFetchAttempted] = useState(false);
  const [latestVersion, setLatestVersion] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(clearErrorMessage());
    };
  }, [dispatch]);

  useEffect(() => {
    if (rigs) {
      const found_node = rigs.find((rig) => rig.rig_uuid === rig_uuid);
      if (found_node) {
        setNode(found_node);
      }
    }
  }, [rigs, rig_uuid]);

  useEffect(() => {
    (async () => {
      if (!provider) {
        await dispatch(fetchActiveProvider(provider_uuid));
        return;
      }
      if (provider && !region) {
        await dispatch(fetchActiveRegion(provider_uuid, region_uuid));
        return;
      }
      if (region && !node && !fetchAttempted) {
        setFetchAttempted(true);
        try {
          await dispatch(fetchRig(provider_uuid, region_uuid, rig_uuid));
        } catch (error) {
          // Error is already handled by the action
          setLoading(false);
          navigate(
            `/resources/provider/${provider_uuid}/region/${region_uuid}/dashboard`,
          );
        }
      }
    })();
  }, [
    dispatch,
    navigate,
    provider,
    region,
    node,
    fetchAttempted,
    provider_uuid,
    region_uuid,
    rig_uuid,
  ]);

  useEffect(() => {
    if (provider && region) {
      if (node) {
        setLoading(false);
      } else if (
        fetchAttempted &&
        rigs !== null &&
        !rigs.find((rig) => rig.rig_uuid === rig_uuid)
      ) {
        // Node not found after fetch attempt - rigs has been populated but node is not in it
        setLoading(false);
        navigate(
          `/resources/provider/${provider_uuid}/region/${region_uuid}/dashboard`,
        );
      }
    }
  }, [
    provider,
    region,
    node,
    rigs,
    fetchAttempted,
    rig_uuid,
    provider_uuid,
    region_uuid,
    navigate,
  ]);

  const handleStartNode = async () => {
    setTransitioning(true);
    await dispatch(
      updateRig(provider_uuid, region_uuid, rig_uuid, {
        command: 'start',
      }),
    );
    setTransitioning(false);
  };

  const handleStopNode = async () => {
    setTransitioning(true);
    await dispatch(
      updateRig(provider_uuid, region_uuid, rig_uuid, {
        command: 'stop',
      }),
    );
    setTransitioning(false);
  };

  const handleEnableRemoteSupport = async () => {
    setTransitioning(true);
    await dispatch(enableRemoteSupport(provider_uuid, region_uuid, rig_uuid));
    setTransitioning(false);
  };

  const handleDisableRemoteSupport = async () => {
    setTransitioning(true);
    await dispatch(disableRemoteSupport(provider_uuid, region_uuid, rig_uuid));
    setTransitioning(false);
  };

  const handleToggleMaintenance = async () => {
    setTransitioning(true);
    await dispatch(toggleMaintenance(provider_uuid, region_uuid, rig_uuid));
    setTransitioning(false);
  };

  const handleUpdateStats = async () => {
    setTransitioning(true);
    await dispatch(runAction(provider_uuid, region_uuid, rig_uuid, 'report'));
    setTransitioning(false);
  };

  const handleEditNode = () => {
    navigate(
      `/resources/provider/${provider_uuid}/region/${region_uuid}/node/${rig_uuid}/edit`,
    );
  };

  const handleRemoveNode = async () => {
    if (node.type === RIG_TYPES.EPHEMERAL) {
      setRemoveEphemeralDialogOpen(true);
      return;
    }
    if (window.confirm('Are you sure you want to delete this node?')) {
      setTransitioning(true);
      await dispatch(deleteRig(provider_uuid, region_uuid, rig_uuid));
      navigate(
        `/resources/provider/${provider_uuid}/region/${region_uuid}/dashboard`,
      );
    }
  };

  const handleRemoveEphemeralNodeClose = () => {
    setRemoveEphemeralDialogOpen(false);
  };

  const handleRemoveEphemeralNodeConfirm = async () => {
    setRemoveEphemeralDialogOpen(false);
    setTransitioning(true);
    await dispatch(deleteRig(provider_uuid, region_uuid, rig_uuid));
    navigate(
      `/resources/provider/${provider_uuid}/region/${region_uuid}/dashboard`,
    );
  };

  const handleActionsMenuOpen = (event) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleReinstallNode = async () => {
    setTransitioning(true);
    setReinstallDialogOpen(false);
    await dispatch(reinstallNode(provider_uuid, region_uuid, rig_uuid));
    setTransitioning(false);
  };

  const handleReinstallDialogOpen = () => {
    setReinstallDialogOpen(true);
    handleActionsMenuClose();
  };

  const handleReinstallDialogClose = () => {
    setReinstallDialogOpen(false);
  };

  const handleReplaceNode = async () => {
    if (minionIdError || !minionId.trim()) {
      return;
    }
    setTransitioning(true);
    setReplaceDialogOpen(false);
    await dispatch(replaceNode(provider_uuid, region_uuid, rig_uuid, minionId));
    setMinionId('');
    setMinionIdError(false);
    setTransitioning(false);
  };

  const handleReplaceDialogOpen = () => {
    setReplaceDialogOpen(true);
    handleActionsMenuClose();
  };

  const handleReplaceDialogClose = () => {
    setReplaceDialogOpen(false);
    setMinionId('');
    setMinionIdError(false);
  };

  const handleUpgradeDialogOpen = async () => {
    setUpgradeDialogOpen(true);
    setLoadingVersion(true);
    setLatestVersion(null);
    handleActionsMenuClose();
    try {
      const version = await dispatch(
        getNodeLatestVersion(provider_uuid, region_uuid),
      );
      setLatestVersion(version);
    } catch (error) {
      // Error is already handled by the action
    } finally {
      setLoadingVersion(false);
    }
  };

  const handleUpgradeDialogClose = () => {
    setUpgradeDialogOpen(false);
    setLatestVersion(null);
    setLoadingVersion(false);
  };

  const handleUpgradeNode = async () => {
    setTransitioning(true);
    setUpgradeDialogOpen(false);
    await dispatch(upgradeNode(provider_uuid, region_uuid, rig_uuid));
    setTransitioning(false);
  };

  const renderStatus = () => {
    if (transitioning) {
      return <CircularProgress size={20} />;
    }
    switch (node.status) {
      case STATUSES.ACTIVE:
        return (
          <Stack direction='row' spacing={1} alignItems='center'>
            <CheckCircleIcon color='success' />
            <Typography variant='body2'>{node.status}</Typography>
          </Stack>
        );
      case STATUSES.OFFLINE:
        return (
          <Stack direction='row' spacing={1} alignItems='center'>
            <StopCircleIcon />
            <Typography variant='body2'>{node.status}</Typography>
          </Stack>
        );
      case STATUSES.STOPPED:
        return (
          <Stack direction='row' spacing={1} alignItems='center'>
            <StopCircleIcon />
            <Typography variant='body2'>{node.status}</Typography>
          </Stack>
        );
      case STATUSES.ERRORED:
        return (
          <Stack direction='row' spacing={1} alignItems='center'>
            <ErrorOutlineIcon color='error' />
            <Typography variant='body2'>{node.status}</Typography>
          </Stack>
        );
      case STATUSES.MAINTENANCE:
        return (
          <Stack direction='row' spacing={1} alignItems='center'>
            <WarningIcon color='warning' />
            <Typography variant='body2'>{node.status}</Typography>
          </Stack>
        );
      default:
        return (
          <Stack direction='row' spacing={1} alignItems='center'>
            <CircularProgress size={20} />
            <Typography variant='body2'>{node.status}</Typography>
          </Stack>
        );
    }
  };

  if (loading) {
    return (
      <Backdrop open={loading}>
        <CircularProgress />
      </Backdrop>
    );
  }

  if (!node) {
    return (
      <Container>
        <Typography variant='h6'>Node not found</Typography>
      </Container>
    );
  }

  return (
    <Container>
          <Breadcrumbs aria-label='breadcrumb'>
            <Link
              underline='hover'
              color='inherit'
              component={RouterLink}
              to={`/dashboard`}
            >
              Home
            </Link>
            <Link
              underline='hover'
              color='inherit'
              component={RouterLink}
              to={`/resources`}
            >
              Resources
            </Link>
            <Link
              underline='hover'
              color='inherit'
              component={RouterLink}
              to={`/resources/provider/${provider_uuid}/region/${region_uuid}/dashboard`}
            >
              {region?.name || 'Region'}
            </Link>
            <Typography color='text.primary'>{node.friendly_name}</Typography>
          </Breadcrumbs>
          <br />
          <Paper sx={{ display: 'flex', width: '100%' }}>
            <Grid container spacing={4} sx={{ flexGrow: 1 }}>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <Paper sx={{ display: 'flex', width: '100%' }}>
                  <Toolbar>
                    <Stack spacing={2} direction='row' alignItems='center'>
                      <Typography variant='h5'>{node.friendly_name}</Typography>
                      {node.config?.remote_support_enabled && (
                        <LockOpenOutlinedIcon
                          color='error'
                          titleAccess='Remote Support is Enabled'
                        />
                      )}
                      <Button onClick={handleEditNode} disabled={transitioning}>
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <EditIcon />
                          <Typography variant='button' display='block'>
                            Edit
                          </Typography>
                        </Stack>
                      </Button>

                      <Button
                        onClick={handleRemoveNode}
                        disabled={transitioning}
                        color='error'
                      >
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <DeleteIcon />
                          <Typography variant='button' display='block'>
                            Remove
                          </Typography>
                        </Stack>
                      </Button>
                      <Button
                        onClick={handleActionsMenuOpen}
                        disabled={transitioning}
                        endIcon={<MoreVertIcon />}
                      >
                        Actions
                      </Button>
                    </Stack>
                  </Toolbar>
                </Paper>
                {node.upgrade_available && (
                  <Alert severity='info' sx={{ mb: 0, mt: 0 }}>
                    A newer version is available for this node.{' '}
                    <Link
                      component='button'
                      variant='body2'
                      onClick={handleUpgradeDialogOpen}
                      sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      Click here to upgrade
                    </Link>
                  </Alert>
                )}
                <Box padding='15px'>
                  <Typography variant='h6'>Basic Information</Typography>
                  <Divider />
                  <br />
                  <Grid
                    container
                    component='dl'
                    spacing={0}
                    sx={{ display: 'table' }}
                  >
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '150px' }}
                      >
                        <b>ID:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {node.rig_uuid}
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '150px' }}
                      >
                        <b>Hostname:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {node.hostname}
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '150px' }}
                      >
                        <b>Status:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {renderStatus()}
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '150px' }}
                      >
                        <b>Type:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {node.type}
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '150px' }}
                      >
                        <b>Service:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {node.service}
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '150px' }}
                      >
                        <b>Provider Type:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {node.provider_type}
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '150px' }}
                      >
                        <b>Online:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {node.online ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '150px' }}
                      >
                        <b>Maintenance Mode:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {node.maintenance_mode ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '150px' }}
                      >
                        <b>Last Active:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {node.last_active
                          ? toNiceTimestamp(node.last_active, true)
                          : 'Unknown'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                <Divider />
                <br />
                <Box padding='15px'>
                  <Typography variant='h6'>Network Information</Typography>
                  <Divider />
                  <br />
                  <Grid
                    container
                    component='dl'
                    spacing={0}
                    sx={{ display: 'table' }}
                  >
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '150px' }}
                      >
                        <b>Public IP:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {node.public_ip || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '150px' }}
                      >
                        <b>Private IP:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {node.private_ip || 'N/A'}
                      </Typography>
                    </Grid>
                    {node.state?.network?.bandwidth && (
                      <>
                        <Grid size="grow">
                          <Typography
                            component='dt'
                            variant='body1'
                            sx={{ display: 'table-cell', width: '150px' }}
                          >
                            <b>Upload Bandwidth:</b>
                          </Typography>
                          <Typography
                            component='dd'
                            variant='body2'
                            sx={{ display: 'table-cell' }}
                          >
                            {node.state.network.bandwidth.upload} Mbps
                          </Typography>
                        </Grid>
                        <Grid size="grow">
                          <Typography
                            component='dt'
                            variant='body1'
                            sx={{ display: 'table-cell', width: '150px' }}
                          >
                            <b>Download Bandwidth:</b>
                          </Typography>
                          <Typography
                            component='dd'
                            variant='body2'
                            sx={{ display: 'table-cell' }}
                          >
                            {node.state.network.bandwidth.download} Mbps
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
                {node.state?.storage && (
                  <>
                    <Divider />
                    <br />
                    <Box padding='15px'>
                      <Typography variant='h6'>Storage Information</Typography>
                      <Divider />
                      <br />
                      <Grid
                        container
                        component='dl'
                        spacing={0}
                        sx={{ display: 'table' }}
                      >
                        <Grid size="grow">
                          <Typography
                            component='dt'
                            variant='body1'
                            sx={{ display: 'table-cell', width: '150px' }}
                          >
                            <b>Hot Storage Used:</b>
                          </Typography>
                          <Typography
                            component='dd'
                            variant='body2'
                            sx={{ display: 'table-cell' }}
                          >
                            {readableBytes(
                              node.state.storage.hot_total -
                                node.state.storage.hot_available,
                            )}
                          </Typography>
                        </Grid>
                        <Grid size="grow">
                          <Typography
                            component='dt'
                            variant='body1'
                            sx={{ display: 'table-cell', width: '150px' }}
                          >
                            <b>Hot Storage Total:</b>
                          </Typography>
                          <Typography
                            component='dd'
                            variant='body2'
                            sx={{ display: 'table-cell' }}
                          >
                            {readableBytes(node.state.storage.hot_total)}
                          </Typography>
                        </Grid>
                        <Grid size="grow">
                          <Typography
                            component='dt'
                            variant='body1'
                            sx={{ display: 'table-cell', width: '150px' }}
                          >
                            <b>Cold Storage Used:</b>
                          </Typography>
                          <Typography
                            component='dd'
                            variant='body2'
                            sx={{ display: 'table-cell' }}
                          >
                            {readableBytes(
                              node.state.storage.cold_total -
                                node.state.storage.cold_available,
                            )}
                          </Typography>
                        </Grid>
                        <Grid size="grow">
                          <Typography
                            component='dt'
                            variant='body1'
                            sx={{ display: 'table-cell', width: '150px' }}
                          >
                            <b>Cold Storage Total:</b>
                          </Typography>
                          <Typography
                            component='dd'
                            variant='body2'
                            sx={{ display: 'table-cell' }}
                          >
                            {readableBytes(node.state.storage.cold_total)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                )}
                <Divider />
                <br />
                <Box padding='15px'>
                  <Typography variant='h6'>Configuration</Typography>
                  <Divider />
                  <br />
                  <Grid
                    container
                    component='dl'
                    spacing={0}
                    sx={{ display: 'table' }}
                  >
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '150px' }}
                      >
                        <b>UPnP Enabled:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {node.config?.upnp_enabled ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '150px' }}
                      >
                        <b>Disable proxiML built-in image download:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {node.config?.proximl_images_disabled ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '150px' }}
                      >
                        <b>Remote Support Enabled:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {node.config?.remote_support_enabled ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                    {node.mining && (
                      <Grid size="grow">
                        <Typography
                          component='dt'
                          variant='body1'
                          sx={{ display: 'table-cell', width: '150px' }}
                        >
                          <b>Mining Enabled:</b>
                        </Typography>
                        <Typography
                          component='dd'
                          variant='body2'
                          sx={{ display: 'table-cell' }}
                        >
                          {node.mining.mining_enabled ? 'Yes' : 'No'}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Paper>
          <Menu
            anchorEl={actionsMenuAnchor}
            open={Boolean(actionsMenuAnchor)}
            onClose={handleActionsMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {provider?.type !== PROVIDER_TYPES.PHYSICAL.value && (
              <>
                <MenuItem
                  onClick={() => {
                    handleStartNode();
                    handleActionsMenuClose();
                  }}
                  disabled={transitioning || node.status !== STATUSES.STOPPED}
                >
                  <ListItemIcon>
                    <PlayCircleOutlineIcon />
                  </ListItemIcon>
                  <ListItemText>Start</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleStopNode();
                    handleActionsMenuClose();
                  }}
                  disabled={
                    transitioning ||
                    ![STATUSES.ACTIVE, STATUSES.MAINTENANCE].includes(
                      node.status,
                    )
                  }
                >
                  <ListItemIcon>
                    <StopCircleOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText>Stop</ListItemText>
                </MenuItem>
              </>
            )}
            <MenuItem
              onClick={() => {
                node.config?.remote_support_enabled
                  ? handleDisableRemoteSupport()
                  : handleEnableRemoteSupport();
                handleActionsMenuClose();
              }}
              disabled={transitioning}
            >
              <ListItemIcon>
                {node.config?.remote_support_enabled ? (
                  <LockOpenOutlinedIcon />
                ) : (
                  <EngineeringIcon />
                )}
              </ListItemIcon>
              <ListItemText>
                {node.config?.remote_support_enabled
                  ? 'Disable Support'
                  : 'Enable Support'}
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleToggleMaintenance();
                handleActionsMenuClose();
              }}
              disabled={transitioning}
            >
              <ListItemIcon>
                <EngineeringIcon />
              </ListItemIcon>
              <ListItemText>Toggle Maintenance</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleUpdateStats();
                handleActionsMenuClose();
              }}
              disabled={transitioning}
            >
              <ListItemIcon>
                <UpdateIcon />
              </ListItemIcon>
              <ListItemText>Update Stats</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={handleReinstallDialogOpen}
              disabled={transitioning}
            >
              <ListItemIcon>
                <ReplayIcon />
              </ListItemIcon>
              <ListItemText>Reinstall Node</ListItemText>
            </MenuItem>
            {provider?.type === PROVIDER_TYPES.PHYSICAL.value && (
              <MenuItem
                onClick={handleReplaceDialogOpen}
                disabled={transitioning || node.status !== STATUSES.MAINTENANCE}
              >
                <ListItemIcon>
                  <SwapHorizIcon />
                </ListItemIcon>
                <ListItemText>Replace Node</ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={handleUpgradeDialogOpen}
              disabled={transitioning || !node.upgrade_available}
            >
              <ListItemIcon>
                <SystemUpdateAltIcon />
              </ListItemIcon>
              <ListItemText>Upgrade Node</ListItemText>
            </MenuItem>
          </Menu>
          <Dialog
            open={reinstallDialogOpen}
            onClose={handleReinstallDialogClose}
            aria-labelledby='reinstall-dialog-title'
            aria-describedby='reinstall-dialog-description'
          >
            <DialogTitle id='reinstall-dialog-title'>
              Reinstall Node
            </DialogTitle>
            <DialogContent>
              <DialogContentText id='reinstall-dialog-description'>
                Reinstalling a node will terminate all running jobs and reset
                the node to an unprovisioned state. This action cannot be undone
                or canceled. Click Continue to proceed.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleReinstallDialogClose} color='primary'>
                Cancel
              </Button>
              <Button
                onClick={handleReinstallNode}
                color='error'
                variant='contained'
                disabled={transitioning}
              >
                Continue
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={replaceDialogOpen}
            onClose={handleReplaceDialogClose}
            aria-labelledby='replace-dialog-title'
            aria-describedby='replace-dialog-description'
            maxWidth='sm'
            fullWidth
          >
            <DialogTitle id='replace-dialog-title'>Replace Node</DialogTitle>
            <DialogContent>
              <DialogContentText id='replace-dialog-description' sx={{ mb: 2 }}>
                Enter the Minion ID of the unassigned system to attach to this
                Node
              </DialogContentText>
              <MinionIdField
                value={minionId}
                error={minionIdError}
                setValue={setMinionId}
                setError={setMinionIdError}
                setChanged={() => {}}
                edit={false}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleReplaceDialogClose} color='primary'>
                Cancel
              </Button>
              <Button
                onClick={handleReplaceNode}
                color='error'
                variant='contained'
                disabled={transitioning || minionIdError || !minionId.trim()}
              >
                Replace
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={upgradeDialogOpen}
            onClose={handleUpgradeDialogClose}
            aria-labelledby='upgrade-dialog-title'
            aria-describedby='upgrade-dialog-description'
          >
            <DialogTitle id='upgrade-dialog-title'>Upgrade Node</DialogTitle>
            <DialogContent>
              <DialogContentText id='upgrade-dialog-description' sx={{ mb: 2 }}>
                <b>Current Version:</b> {node.rig_version || 'Unknown'}
              </DialogContentText>
              <DialogContentText sx={{ mb: 2 }}>
                <b>Latest Version:</b>{' '}
                {loadingVersion ? (
                  <CircularProgress size={16} sx={{ ml: 1 }} />
                ) : (
                  latestVersion || 'Unknown'
                )}
              </DialogContentText>
              {!node.maintenance_mode && (
                <Alert severity='warning' sx={{ mb: 2 }}>
                  The node must be in maintenance mode before upgrading. Please
                  enable maintenance mode first.
                </Alert>
              )}
              <DialogContentText color='error' sx={{ mt: 2 }}>
                <b>Warning:</b> Upgrading a node will stop any running jobs
                during the upgrade process. The node may reboot until the
                upgrade is complete.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleUpgradeDialogClose} color='primary'>
                Cancel
              </Button>
              <Button
                onClick={handleUpgradeNode}
                color='brand'
                variant='contained'
                disabled={
                  transitioning || loadingVersion || !node.maintenance_mode
                }
              >
                Upgrade
              </Button>
            </DialogActions>
          </Dialog>
          <RemoveEphemeralNodeDialog
            open={removeEphemeralDialogOpen}
            onClose={handleRemoveEphemeralNodeClose}
            onConfirm={handleRemoveEphemeralNodeConfirm}
          />
          <ErrorSnackBar message={api_error} />
        </Container>
  );
}
